import type { LinhaHierarquia } from '@baiocchi/shared'
import {
  Alerta,
  Botao,
  CabecalhoCartao,
  Cartao,
  Esqueleto,
  EstadoVazio,
  Etiqueta,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeFaisca } from '../../componentes/icones'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import {
  AvisoDefasagem,
  BadgeFadiga,
  BadgeScore,
  CartaoDeMetrica,
  PainelSaude,
  formatarMetrica,
} from './componentes'
import { FiltrosMetaAds, parametrosDoFiltro } from './filtros'

/**
 * TELA-META-01 — Visão geral.
 *
 * A tela responde, em ordem de leitura, as perguntas do §26: *a conta está saudável?*,
 * *quanto investi e quanto rendeu?*, *o que a IA aponta?*, *o que está indo melhor e pior?*
 *
 * Uma requisição só (`GET /meta-ads/painel`) monta tudo — §25. A alternativa (um endpoint
 * por card) faria a abertura da tela disparar dez chamadas, cada uma repetindo a mesma
 * consulta de insights para recortar um número diferente.
 */
export function PaginaMetaAdsVisaoGeral() {
  const { filtros, definir } = useFiltrosUrl()
  const parametros = parametrosDoFiltro(filtros)

  const contas = useQuery({
    queryKey: chaves.metaAds.contas,
    queryFn: ({ signal }) => metaAdsApi.contas(signal),
    staleTime: 300_000,
  })

  const consulta = useQuery({
    queryKey: chaves.metaAds.painel(parametros),
    queryFn: ({ signal }) => metaAdsApi.painel(parametros, signal),
    // 60 s: os insights vêm de sincronização horária, então revalidar mais rápido gastaria
    // requisição para receber o mesmo número.
    staleTime: 60_000,
  })

  const dados = consulta.data?.dados
  const meta = consulta.data?.meta

  return (
    <>
      <CabecalhoPagina
        titulo="Meta Ads"
        descricao="Investimento, resultado e o que otimizar nas contas de anúncio."
        acoes={
          <Link to="/meta-ads/campanhas">
            <Botao variante="contorno" tamanho="sm">
              Ver campanhas
            </Botao>
          </Link>
        }
      />

      <FiltrosMetaAds
        filtros={filtros}
        definir={definir}
        contas={contas.data?.dados ?? []}
        carregandoContas={contas.isLoading}
      />

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}

      {consulta.isLoading ? <Esqueleto className="h-64 w-full" /> : null}

      {dados !== undefined ? (
        <>
          {/*
            Duas mensagens diferentes para duas situações diferentes: nunca sincronizou é
            configuração pendente; sincronizou e não há dado é conta sem veiculação. Uma
            mensagem só faria o gestor procurar o problema errado.
          */}
          {dados.sincronizacaoPendente ? (
            <Alerta tom="info" className="mb-4">
              Nenhuma sincronização com a Meta foi concluída ainda. Cadastre as contas de
              anúncio e rode a primeira sincronização em{' '}
              <Link to="/meta-ads/contas" className="underline">
                Contas
              </Link>
              .
            </Alerta>
          ) : dados.semDados ? (
            <Alerta tom="atencao" className="mb-4">
              Não há métricas sincronizadas para este período. Se as campanhas estavam
              ativas, confira se a última sincronização registrou erro em Contas.
            </Alerta>
          ) : null}

          <div className="mb-4 grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <PainelSaude saude={dados.saude} />
            </div>
            <div className="lg:col-span-2">
              <ResumoInsights insights={dados.insights} />
            </div>
          </div>

          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {dados.cartoes.map((cartao) => (
              <CartaoDeMetrica key={cartao.chave} cartao={cartao} />
            ))}
          </div>

          <div className="mb-4">
            <SerieDiaria serie={dados.serie} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <RankingAnuncios
              titulo="Melhores anúncios"
              descricao="Maior score no período — onde o §10 sugere colocar mais dinheiro."
              linhas={dados.melhoresAnuncios}
            />
            <RankingAnuncios
              titulo="Piores anúncios"
              descricao="Menor score no período — candidatos a revisão ou pausa."
              linhas={dados.pioresAnuncios}
            />
          </div>

          <div className="mt-4">
            <AvisoDefasagem
              defasagemMinutos={meta?.defasagemMinutos ?? 0}
              atualizadoEm={meta?.atualizadoEm}
            />
          </div>
        </>
      ) : null}
    </>
  )
}

/**
 * O bloco de insights do §26.
 *
 * `iaDisponivel: false` **explica** a ausência em vez de esconder a seção. Um espaço vazio
 * pareceria defeito; a contagem de oportunidades e alertas continua aparecendo, porque ela
 * é determinística e não depende da IA.
 */
function ResumoInsights({
  insights,
}: {
  readonly insights: {
    readonly narrativa: string | null
    readonly oportunidades: number
    readonly alertas: number
    readonly candidatosOtimizacao: number
    readonly iaDisponivel: boolean
  }
}) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo={
          <span className="inline-flex items-center gap-2">
            <IconeFaisca className="h-5 w-5 text-marca-600" />
            Insights
          </span>
        }
        descricao={
          <span className="flex flex-wrap gap-2">
            <Etiqueta tom="sucesso">{insights.oportunidades} oportunidades</Etiqueta>
            <Etiqueta tom="perigo">{insights.alertas} alertas</Etiqueta>
            <Etiqueta tom="atencao">
              {insights.candidatosOtimizacao} candidatos a otimização
            </Etiqueta>
          </span>
        }
        acoes={
          <Link to="/meta-ads/recomendacoes">
            <Botao variante="fantasma" tamanho="sm">
              Ver recomendações
            </Botao>
          </Link>
        }
      />
      <div className="p-4 sm:p-5">
        {insights.narrativa !== null ? (
          <p className="whitespace-pre-line text-sm leading-relaxed text-conteudo">
            {insights.narrativa}
          </p>
        ) : insights.iaDisponivel ? (
          <p className="text-sm text-conteudo-suave">
            A análise em texto não está disponível para este recorte. As contagens acima e as
            recomendações continuam valendo.
          </p>
        ) : (
          <p className="text-sm text-conteudo-suave">
            A análise em texto está desativada neste ambiente. Métricas, score, diagnóstico e
            recomendações continuam funcionando — a IA acrescenta apenas a explicação em
            linguagem de negócio.
          </p>
        )}
      </div>
    </Cartao>
  )
}

/**
 * Série diária de investimento e leads.
 *
 * Barras em CSS puro, sem biblioteca de gráficos: são duas séries de no máximo 30 pontos, e
 * uma dependência de charting custaria mais no pacote do que este componente inteiro — a
 * mesma decisão que `icones.tsx` documenta para ícones.
 *
 * A altura é proporcional ao maior valor do próprio período, não a uma escala fixa: o que
 * o gestor lê aqui é a **forma** da curva (que dia caiu, que dia subiu), e uma escala
 * absoluta achataria a série inteira de uma conta pequena.
 */
function SerieDiaria({
  serie,
}: {
  readonly serie: ReadonlyArray<{
    readonly data: string
    readonly gasto: number
    readonly leads: number
    readonly cpl: number | null
  }>
}) {
  if (serie.length === 0) return null

  const maiorGasto = Math.max(1, ...serie.map((ponto) => ponto.gasto))
  const maiorLeads = Math.max(1, ...serie.map((ponto) => ponto.leads))

  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo="Investimento e leads por dia"
        descricao="Barra cheia = investimento; marca clara = leads. Escala relativa ao período."
      />
      <div className="overflow-x-auto p-4 sm:p-5">
        <div className="flex min-w-[32rem] items-end gap-1" role="img" aria-label="Série diária">
          {serie.map((ponto) => (
            <div key={ponto.data} className="flex flex-1 flex-col items-center gap-1">
              <div className="relative flex h-32 w-full items-end justify-center">
                <div
                  className="w-full rounded-t bg-marca-500/70"
                  style={{ height: `${Math.round((ponto.gasto / maiorGasto) * 100)}%` }}
                  title={`${ponto.data} · investimento ${formatarMetrica(ponto.gasto, 'MOEDA')}`}
                />
                <div
                  className="absolute bottom-0 w-1/2 rounded-t bg-sucesso/80"
                  style={{ height: `${Math.round((ponto.leads / maiorLeads) * 100)}%` }}
                  title={`${ponto.data} · ${ponto.leads} leads · CPL ${formatarMetrica(ponto.cpl, 'MOEDA')}`}
                />
              </div>
              <span className="text-[0.625rem] tabular-nums text-conteudo-fraco">
                {ponto.data.slice(8, 10)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Cartao>
  )
}

function RankingAnuncios({
  titulo,
  descricao,
  linhas,
}: {
  readonly titulo: string
  readonly descricao: string
  readonly linhas: ReadonlyArray<LinhaHierarquia>
}) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao titulo={titulo} descricao={descricao} />
      <div className="p-4 sm:p-5">
        {linhas.length === 0 ? (
          <EstadoVazio
            titulo="Sem anúncios avaliáveis"
            descricao="Nenhum anúncio teve volume suficiente no período para receber nota."
          />
        ) : (
          <ul className="space-y-3">
            {linhas.map((linha) => (
              <li key={linha.idExterno} className="border-b border-borda pb-3 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-conteudo">
                    {linha.nome}
                  </p>
                  <BadgeScore score={linha.score} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-conteudo-suave">
                  <span>Investimento {formatarMetrica(linha.metricas.gasto, 'MOEDA')}</span>
                  <span>{linha.metricas.leads} leads</span>
                  <span>CPL {formatarMetrica(linha.metricas.cpl, 'MOEDA')}</span>
                  <BadgeFadiga fadiga={linha.fadiga} />
                </div>
                {/*
                  A atribuição no CRM é o diferencial do §29 e aparece aqui, não escondida
                  numa aba: "80 leads, 3 vendas" é a informação que muda a decisão, e o Ads
                  Manager não sabe dizer isso.
                */}
                {linha.crm !== null ? (
                  <p className="mt-1 text-xs text-conteudo-suave">
                    No CRM: {linha.crm.leadsNoCrm} leads · {linha.crm.vendas} venda(s)
                    {linha.crm.custoPorVenda !== null
                      ? ` · custo por venda ${formatarMetrica(linha.crm.custoPorVenda, 'MOEDA')}`
                      : ''}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Cartao>
  )
}
